// @ts-ignore - We need to revisit these module types. The @types/braintree-web types aren't correct and creating our own module types seems like a daunting task right now.
import Client from 'braintree-web/client';
// @ts-ignore
import PaypalCheckout from 'braintree-web/paypal-checkout';
import React, { Component } from 'react';
import { prop } from 'lodash/fp';

import {
  PaymentDetailsAddress,
  PaymentSourceInput,
  PaymentProcessor,
  PaymentSourceType,
} from '@zapier/payment/src/types';

import PaypalButtonPlaceholder from './PaypalButtonPlaceholder';

import { PAYPAL_BUTTON_ID } from './constants';

type Props = {
  /** Paypal API Key */
  apiKey: string;
  /** Call when Paypal has authorized user */
  onAuthorize: (a: PaymentSourceInput) => void;
  /** Call when Paypal returns an error */
  onError: (a: Error) => void;
};

type State = {
  isLoading: boolean;
};

type PaypalCheckoutPayload = {
  billingAddress: PaymentDetailsAddress;
  email: string;
  nonce: string;
};

type PaypalCheckoutType = {
  createPayment: (a: {}) => {};
  tokenizePayment: (a: {}) => Promise<PaypalCheckoutPayload>;
};

const PAYMENT_DESC = {
  billingAgreementDescription: `
    You authorize Zapier to charge your PayPal account for your chosen subscription at
    your specified billing interval.
    By continuing, you understand and agree to these terms and Zapier’s privacy policy
    and terms of service.
  `,
  flow: 'vault',
};

const BUTTON_STYLE = {
  color: 'blue',
  label: 'paypal',
  shape: 'rect',
  size: 'responsive',
  height: 49,
  tagline: false,
};

class PaypalButton extends Component<Props, State> {
  paypalCheckout!: PaypalCheckoutType;

  state = {
    isLoading: true,
  };

  createPayment = () => {
    return this.paypalCheckout.createPayment(PAYMENT_DESC);
  };

  handleAuthorize = async (data: {}) => {
    const payload = await this.paypalCheckout.tokenizePayment(data);
    const billingAddress = prop(['details', 'billingAddress'], payload);

    const paymentSource = {
      details: {
        addressCountry: billingAddress.countryCode,
        addressState: billingAddress.state,
        addressZip: billingAddress.postalCode,
        email: prop(['details', 'email'], payload),
      },
      method: PaymentSourceType.paypal,
      paymentProcessor: PaymentProcessor.braintree,
      token: payload.nonce,
    };
    this.props.onAuthorize(paymentSource);
  };

  handleError = (error: Error) => {
    this.props.onError(error);
  };

  async componentDidMount() {
    const { apiKey } = this.props;

    const client = await Client.create({
      authorization: apiKey,
    });

    this.paypalCheckout = await PaypalCheckout.create({
      client,
    });

    this.setState({
      isLoading: false,
    });

    /**
     * `paypal-checkout` requires a window object, and this component can get included
     * in the web-ssr build, which has no window object, due to a lack of tree shaking
     * in local dev environments.
     *
     * See https://zapier.slack.com/archives/CT3496UEL/p1584023571112600 for context.
     */

    if (window) {
      // @ts-ignore
      const paypal = (await import('paypal-checkout')).default;
      await paypal.Button.render(
        {
          env: apiKey.split('_')[0],
          onAuthorize: this.handleAuthorize,
          onError: this.handleError,
          payment: this.createPayment,
          style: BUTTON_STYLE,
        },
        `#${PAYPAL_BUTTON_ID}`
      );
    }
  }

  render() {
    return <PaypalButtonPlaceholder isLoading={this.state.isLoading} />;
  }
}

export default PaypalButton;
